import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { HelmetProvider } from 'react-helmet-async';
import App from './App';
import ErrorBoundary from './components/ErrorBoundary';
import { AuthProvider } from './contexts/AuthContext';
import { initSentry } from './utils/sentry';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { captureError } from './utils/sentry';
import { registerServiceWorker } from './service-worker/register';
import './index.css';

// Initialize Sentry
initSentry();

// Initialize Service Worker with modern update handling
if (process.env.NODE_ENV === 'production') {
  registerServiceWorker({
    onSuccess: () => {
      debug('Service Worker registered successfully');
    },
    onUpdate: () => {
      // Use react-toastify for update notification
      toast.info('MovieHub new version available! Will update on next visit.', {
        position: "bottom-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    }
  }).catch(error => {
    captureError(error, {
      feature: 'serviceWorker',
      type: 'registration_error'
    });
  });
}


// Initialize React Query with better error handling
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 3,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      staleTime: 300000, // 5 minutes
      cacheTime: 3600000, // 1 hour
      refetchOnWindowFocus: false,
      onError: (error) => {
        if (process.env.NODE_ENV === 'production') {
          captureError(error, {
            feature: 'query',
            type: 'query_error'
          });
        } else {
          console.error('Query error:', error);
        }
      },
    },
    mutations: {
      onError: (error) => {
        if (process.env.NODE_ENV === 'production') {
          captureError(error, {
            feature: 'mutation',
            type: 'mutation_error'
          });
        } else {
          console.error('Mutation error:', error);
        }
      },
    },
  },
});

// Remove console.log in production
if (process.env.NODE_ENV === 'production') {
  console.log = () => {};
  // Keep console.warn and console.error for critical issues
}

// Debug utility
const debug = (...args) => {
  if (process.env.NODE_ENV !== 'production') {
    console.log(...args);
  }
};

ReactDOM.createRoot(document.getElementById('root')).render(
  <React.StrictMode>
    <ErrorBoundary>
      <HelmetProvider>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter>
            <AuthProvider>
              <App />
              <ToastContainer 
                position="top-right"
                autoClose={5000}
                hideProgressBar={false}
                newestOnTop
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="light"
              />
            </AuthProvider>
          </BrowserRouter>
        </QueryClientProvider>
      </HelmetProvider>
    </ErrorBoundary>
  </React.StrictMode>
);
